import {
  BpkCardV2,
  CARD_V2_VARIANTS,
} from '@skyscanner-internal/backpack-web/bpk-component-card';
import {
  BpkBox,
  BpkProvider,
  BpkSpacing,
  BpkStack,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';

import {
  MONTH_VIEW_RHR_CTA_CONTAINER,
  STREVDA_CONTAINERS,
} from '../../constants';
import { GlobalCookieBanner } from '../GlobalCookieBanner/GlobalCookieBanner';
import GlobalHeader from '../GlobalHeader';
import AppErrorBoundary from '../MonthView/components/AppErrorBoundary';
import { TOTAL_TRIP_COST_POSITION_CHANGED_BREAKPOINT } from '../MonthView/helpers/constants/displayConstants';
import { useMediaQuery } from '../MonthView/helpers/hooks/useMediaQuery';
import MonthViewContainer from '../MonthViewContainer';

import { ItineraryVisibilityProvider } from './ItineraryVisibilityContext';

import type { InternalProps } from '../../types/globals';

const StrevdaSidebar = ({ id }: { id: string }) => (
  <BpkBox id={id} width="100%" />
);

const App = ({
  adultsV2,
  adverts,
  browse,
  cabinclass,
  childrenV2,
  destinationCityName,
  destinationId,
  destinationIds,
  destinationName,
  device,
  fareAttributes,
  inboundAlts,
  inboundDate,
  inboundDay,
  localisation,
  originId,
  originIds,
  originName,
  outboundAlts,
  outboundDate,
  outboundDay,
  preferDirects,
  stay,
  translations,
  tripType,
  viewId,
}: InternalProps) => {
  const isMobilePhone = device?.isMobilePhone;
  const isTablet = device?.isTablet;
  const enableDesktopRightRail = !isMobilePhone && !isTablet;
  const enableSidebarOnDesktop =
    adverts?.enabled && !isMobilePhone && !isTablet;
  const isDesktopRightRailVisible = useMediaQuery(
    TOTAL_TRIP_COST_POSITION_CHANGED_BREAKPOINT,
  );
  const showDesktopRightRail =
    enableDesktopRightRail && isDesktopRightRailVisible;

  return (
    <BpkProvider>
      <ItineraryVisibilityProvider>
        <AppErrorBoundary location="global-header">
          <GlobalHeader />
        </AppErrorBoundary>
        <AppErrorBoundary location="global-cookie-banner">
          <GlobalCookieBanner />
        </AppErrorBoundary>
        <BpkStack
          direction="row"
          justify="space-between"
          gap={BpkSpacing.Base}
          position="relative"
          width={
            showDesktopRightRail ? '100%' : { base: '100%', tablet: '95%' }
          }
          maxWidth="65.5rem"
          marginInline="auto"
          marginBlockEnd={BpkSpacing.XL}
        >
          <BpkBox width={showDesktopRightRail ? '45.5rem' : '100%'}>
            <AppErrorBoundary location="month-view-container">
              <MonthViewContainer
                originId={originId}
                originName={originName}
                destinationId={destinationId}
                destinationName={destinationName}
                destinationCityName={destinationCityName}
                device={device}
                tripType={tripType}
                outboundDate={outboundDate}
                inboundDate={inboundDate}
                outboundDay={outboundDay}
                inboundDay={inboundDay}
                outboundAlts={outboundAlts}
                inboundAlts={inboundAlts}
                localisation={localisation}
                preferDirects={preferDirects}
                adultsV2={adultsV2}
                cabinclass={cabinclass}
                childrenV2={childrenV2}
                translations={translations}
                browse={browse}
                stay={stay}
                destinationEntityId={destinationIds?.entityId}
                originEntityId={originIds?.entityId}
                viewId={viewId}
                fareAttributes={fareAttributes}
              />
            </AppErrorBoundary>
            {adverts?.enabled &&
              (device?.isMobilePhone ? (
                <AppErrorBoundary location="strevda-mobile-mpu">
                  <BpkBox
                    id={STREVDA_CONTAINERS.MOBILE_MPU}
                    width="18.75rem"
                    marginTop={BpkSpacing.SM}
                    marginInline="auto"
                  />
                </AppErrorBoundary>
              ) : (
                <AppErrorBoundary location="strevda-desktop-banner">
                  <BpkBox
                    id={STREVDA_CONTAINERS.BANNER}
                    marginTop={BpkSpacing.SM}
                  />
                </AppErrorBoundary>
              ))}
          </BpkBox>
          {enableDesktopRightRail && (
            <BpkBox
              as="aside"
              display={showDesktopRightRail ? 'flex' : 'none'}
              width="18.75rem"
              flex="0 0 18.75rem"
              marginTop={BpkSpacing.XXXL}
            >
              <BpkStack direction="column" gap={BpkSpacing.SM} width="100%">
                <BpkBox marginBlockEnd={BpkSpacing.Base}>
                  <BpkCardV2.Root variant={CARD_V2_VARIANTS.noElevation}>
                    <BpkCardV2.Body
                      id={MONTH_VIEW_RHR_CTA_CONTAINER}
                      padding={BpkSpacing.Base}
                    >
                      {null}
                    </BpkCardV2.Body>
                  </BpkCardV2.Root>
                </BpkBox>
                {enableSidebarOnDesktop && (
                  <AppErrorBoundary location="strevda-sidebar">
                    <StrevdaSidebar id={STREVDA_CONTAINERS.SIDEBAR} />
                  </AppErrorBoundary>
                )}
              </BpkStack>
            </BpkBox>
          )}
        </BpkStack>
      </ItineraryVisibilityProvider>
    </BpkProvider>
  );
};

export default App;
